import { ActionButtonTypeName } from "./actionTypeConfig";

/**
 * What each button type requires of the operator before anything is sent.
 *
 * The button TYPE encodes an interaction protocol — how many deliberate acts,
 * and what each one sends. Until now that protocol existed nowhere as data: it
 * was implicit across nine button components, each with its own click handler,
 * timers and paired-command handling. So a question like "may selecting a floor
 * be the sending act?" had no place to be answered, and had to be re-derived per
 * type. Re-deriving is how those nine drifted apart in the first place.
 *
 * This is the dispatch-side counterpart of the wizard's BUTTON_TYPE_CAPS, and
 * follows the same rule for the same reason: adding a button type means adding
 * a row here, not editing a predicate and its call sites. The completeness test
 * makes the row mandatory rather than merely advisable.
 */

/** One button type's dispatch protocol. */
export type DispatchProtocol = {
  /**
   * May selecting a floor itself BE the act that sends?
   *
   * True only where the type has exactly one deliberate operator act, and that
   * act sends exactly one unambiguous command. Everything else needs a second,
   * explicit press regardless of the configured firing mode — the mode governs
   * whether a floor tap can send, never whether a type's own protocol may be
   * skipped.
   */
  floorSelectionCanSend: boolean;
  /**
   * Why not, when false.
   *
   * Recorded so the next person deciding this does not have to reconstruct the
   * reasoning from nine components. Omitted where the answer is yes.
   */
  reason?: string;
};

export const DISPATCH_PROTOCOL: Record<ActionButtonTypeName, DispatchProtocol> = {
  // One button, one command. The floor tap and the press are the same act, so
  // making the tap send removes a step rather than skipping a safeguard.
  SINGLE: { floorSelectionCanSend: true },

  // One button, one command, and the CONTROLLER clears it. Same shape as
  // SINGLE from the operator's side — the difference is who owns the off edge,
  // which does not change how the command is sent.
  ONE_SHOT: { floorSelectionCanSend: true },

  ON_OFF: {
    floorSelectionCanSend: false,
    reason:
      "Two buttons for the two edges of one command. A floor tap would have to " +
      "pick on or off, and neither is 'the' command.",
  },

  TOGGLE: {
    floorSelectionCanSend: false,
    reason:
      "Two buttons sending two DIFFERENT commands — primary and paired toggle. " +
      "There is no single command a floor tap could stand for.",
  },

  TOGGLETIMER: {
    floorSelectionCanSend: false,
    reason: "TOGGLE's two commands plus a timer; the same ambiguity, with state.",
  },

  SWITCH: {
    floorSelectionCanSend: false,
    reason:
      "A primary command plus an optional timed off-sequence. The sequence is " +
      "the point of the type, and a floor tap cannot express which part is wanted.",
  },

  HOLDBACK: {
    floorSelectionCanSend: false,
    reason:
      "The operator must HOLD the confirm button for `timer` seconds. That hold " +
      "IS the safeguard, so firing from a floor tap would delete the type's reason " +
      "to exist.",
  },

  TURNBACK: {
    floorSelectionCanSend: false,
    reason:
      "One press, but it ARMS a timed auto-revert rather than sending and " +
      "finishing. Excluded deliberately: a naive one-press test would include it, " +
      "and arming an unattended reversal from a floor tap is more surprising than " +
      "it is quick.",
  },

  ENERGIZER_TURN_BACK: {
    floorSelectionCanSend: false,
    reason: "A specialised TURNBACK, excluded for the same reason.",
  },
};

/**
 * May a floor tap send this row's command?
 *
 * Thin accessor over the table, matching how BUTTON_TYPE_CAPS is read. Call
 * sites ask this rather than testing the type themselves, so a new type cannot
 * quietly inherit another's answer.
 */
export function floorSelectionCanSend(type: ActionButtonTypeName | undefined): boolean {
  if (!type) return false;

  return DISPATCH_PROTOCOL[type]?.floorSelectionCanSend ?? false;
}
